import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Input,
  Space,
  Tag,
  Button,
  message,
  Modal,
  Descriptions,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import userService from "../../services/userService";
import { useCommon } from "../../context/CommonContext";
const { Search } = Input;

const UserList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("Website Users");
  }, []);

  // State management
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Auth-account detail modal
  const [authModal, setAuthModal] = useState({ open: false, account: null });
  const searchDebounceRef = React.useRef(null);
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = React.useRef(0);
  const DEBOUNCE_MS = 300;
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
  });

  // Filters state
  const [filters, setFilters] = useState({
    id: "",
    firstname: "",
    lastname: "",
    mobile_code: "",
    mobile: "",
    email: "",
    user_status: null,
    is_deleted: null,
  });

  // Server-side sort state ({ field, order } where order is antd's ascend/descend)
  const [sortState, setSortState] = useState({ field: null, order: null });

  // Per-field search params, empty boxes omitted
  const searchParams = (f) => ({
    id: f.id?.trim() || null,
    firstname: f.firstname?.trim() || null,
    lastname: f.lastname?.trim() || null,
    mobile_code: f.mobile_code?.trim() || null,
    mobile: f.mobile?.trim() || null,
    email: f.email?.trim() || null,
  });

  // Sort params for the API (empty when no active sort)
  const sortParams = (s) =>
    s.field && s.order
      ? { sort_by: s.field, sort_order: s.order === "ascend" ? "asc" : "desc" }
      : {};

  // Table columns configuration
  const userColumns = [
    {
      title: "ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 80,
      sorter: true,
      sortOrder: sortState.field === "user_id" ? sortState.order : null,
    },
    {
      title: "First Name",
      dataIndex: "user_firstname",
      key: "user_firstname",
      render: (text) => text || "-",
      sorter: true,
      sortOrder: sortState.field === "user_firstname" ? sortState.order : null,
    },
    {
      title: "Last Name",
      dataIndex: "user_lastname",
      key: "user_lastname",
      render: (text) => text || "-",
      sorter: true,
      sortOrder: sortState.field === "user_lastname" ? sortState.order : null,
    },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      sorter: true,
      sortOrder: sortState.field === "user_email" ? sortState.order : null,
    },
    {
      title: "Mobile Code",
      dataIndex: "user_country_code",
      key: "user_country_code",
      render: (text) => text || "-",
    },
    {
      title: "Mobile Number",
      dataIndex: "user_mobile",
      key: "user_mobile",
      render: (text) => text || "-",
      sorter: true,
      sortOrder: sortState.field === "user_mobile" ? sortState.order : null,
    },
    {
      title: "Gender",
      dataIndex: "user_gender",
      key: "user_gender",
      width: 100,
      // Stored inconsistently across signup paths ("male", "Male", "female",
      // "Female"), so it is capitalised for display rather than shown raw.
      render: (text) =>
        text ? String(text).charAt(0).toUpperCase() + String(text).slice(1).toLowerCase() : "-",
    },
    {
      title: "DOB",
      dataIndex: "user_dob",
      key: "user_dob",
      width: 120,
      // Already formatted DD-MM-YYYY by the API, same as Creation Date.
      render: (text) => text || "-",
    },
    {
      title: "Auth Account",
      dataIndex: "auth_accounts",
      key: "auth_accounts",
      render: (accounts) =>
        Array.isArray(accounts) && accounts.length
          ? accounts.map((acc) => (
              <Tag
                color="blue"
                key={acc.id}
                style={{ textTransform: "capitalize", cursor: "pointer" }}
                onClick={() => setAuthModal({ open: true, account: acc })}
              >
                {acc.provider}
              </Tag>
            ))
          : "-",
    },
    {
      title: "User Status",
      dataIndex: "user_is_active",
      key: "user_is_active",
      render: (text) => (
        <Tag color={text === "Y" ? "green" : "red"}>
          {text === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "Y" },
        { text: "Inactive", value: "N" },
      ],
      filterMultiple: false,
    },
    {
      title: "User Deleted",
      dataIndex: "is_user_deleted",
      key: "is_user_deleted",
      render: (text) => (
        <Tag color={text === "Y" ? "red" : "green"}>
          {text === "Y" ? "Yes" : "No"}
        </Tag>
      ),
      filters: [
        { text: "Yes", value: "Y" },
        { text: "No", value: "N" },
      ],
      filterMultiple: false,
    },
    {
      title: "Creation Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/website-users/details/${record.user_id}`)}
            size="small"
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  // Fetch users data
  const getUsers = async (params = {}) => {
    const requestId = ++requestSeqRef.current;
    try {
      setLoading(true);
      const queryParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        ...params,
      };
      // Drop empty filters so they don't reach the API as the string "null"
      Object.keys(queryParams).forEach(
        (key) => queryParams[key] == null && delete queryParams[key]
      );

      console.log("Fetching users with params:", queryParams);
      const response = await userService.getUsers(queryParams);
      if (requestId !== requestSeqRef.current) return; // superseded by a newer request
      console.log("API Response:", response);

      const { data } = response;

      if (data && data.status) {
        setUserList(data.data.data || []);
        setPagination((prev) => ({
          ...prev,
          ...data.data.pagination,
          current: data.data.pagination.currentPage,
        }));
      } else {
        console.log("API returned no data or status false:", data);
        setUserList([]);
      }
    } catch (error) {
      if (requestId !== requestSeqRef.current) return;
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
      setUserList([]);
    } finally {
      if (requestId === requestSeqRef.current) setLoading(false);
    }
  };

  const applyFilters = (patch = {}) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPagination((prev) => ({ ...prev, current: 1 }));
    getUsers({
      ...searchParams(next),
      user_status: next.user_status,
      is_deleted: next.is_deleted,
      ...sortParams(sortState),
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Debounced typing in a search box
  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      applyFilters({ [field]: value });
      searchDebounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately
  const handleFieldSearch = (field) => (value) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    applyFilters({ [field]: value });
  };

  // Handle table change (pagination, column filters). Column filters are
  // applied server-side because the table is paginated server-side.
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const { current, pageSize } = newPagination;
    const user_status = tableFilters?.user_is_active?.[0] ?? null;
    const is_deleted = tableFilters?.is_user_deleted?.[0] ?? null;
    // sorter.order is undefined when the user clears the sort
    const sortField = sorter?.order ? sorter.field : null;
    const sortOrder = sorter?.order ?? null;

    const filtersChanged =
      user_status !== filters.user_status || is_deleted !== filters.is_deleted;
    const sortChanged =
      sortField !== sortState.field || sortOrder !== sortState.order;
    const pageChanged =
      current !== pagination.current || pageSize !== pagination.pageSize;

    if (!filtersChanged && !sortChanged && !pageChanged) return;

    // Filter or sort changes reset to page 1; a pure page change keeps position
    const nextPage = filtersChanged || sortChanged ? 1 : current;
    const nextSort = { field: sortField, order: sortOrder };
    setFilters((prev) => ({ ...prev, user_status, is_deleted }));
    setSortState(nextSort);
    setPagination((prev) => ({ ...prev, current: nextPage, pageSize }));

    getUsers({
      ...searchParams(filters),
      user_status,
      is_deleted,
      ...sortParams(nextSort),
      perPage: pageSize,
      currentPage: nextPage,
    });
  };

  // Load data on component mount
  useEffect(() => {
    getUsers();
  }, []);

  // Header content with user info

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  return (
    <Card>
      {/* Table Operations */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Search
          placeholder="ID"
          style={{ width: 110 }}
          value={filters.id}
          onChange={handleFieldChange("id")}
          onSearch={handleFieldSearch("id")}
          allowClear
        />
        <Search
          placeholder="First name"
          style={{ width: 170 }}
          value={filters.firstname}
          onChange={handleFieldChange("firstname")}
          onSearch={handleFieldSearch("firstname")}
          allowClear
        />
        <Search
          placeholder="Last name"
          style={{ width: 170 }}
          value={filters.lastname}
          onChange={handleFieldChange("lastname")}
          onSearch={handleFieldSearch("lastname")}
          allowClear
        />
        <Search
          placeholder="Mobile code"
          style={{ width: 175 }}
          value={filters.mobile_code}
          onChange={handleFieldChange("mobile_code")}
          onSearch={handleFieldSearch("mobile_code")}
          allowClear
        />
        <Search
          placeholder="Mobile number"
          style={{ width: 180 }}
          value={filters.mobile}
          onChange={handleFieldChange("mobile")}
          onSearch={handleFieldSearch("mobile")}
          allowClear
        />
        <Search
          placeholder="Email"
          style={{ width: 220 }}
          value={filters.email}
          onChange={handleFieldChange("email")}
          onSearch={handleFieldSearch("email")}
          allowClear
        />
      </div>

      {/* Data Table */}
      <Table
        columns={userColumns}
        dataSource={userList}
        rowKey="user_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
        size="middle"
      />

      {/* Auth Account details modal */}
      <Modal
        title="Auth Account Details"
        open={authModal.open}
        onCancel={() => setAuthModal({ open: false, account: null })}
        footer={null}
      >
        {authModal.account && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Provider">
              <span style={{ textTransform: "capitalize" }}>
                {authModal.account.provider || "-"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Provider Account ID">
              {authModal.account.providerAccountId || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {authModal.account.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Relay Email">
              {authModal.account.relayEmail || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Expires At">
              {authModal.account.expires_at ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Active">
              <Tag color={authModal.account.is_active === "Y" ? "green" : "red"}>
                {authModal.account.is_active === "Y" ? "Yes" : "No"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {authModal.account.createdAt || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {authModal.account.updatedAt || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default UserList;
